import React from 'react';
import SEO from '../components/SEO';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Container,
  SimpleGrid,
  Icon,
  Flex,
  useColorModeValue,
  Image,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaArrowRight, FaCode, FaMicrochip, FaWrench } from 'react-icons/fa';

const Home = () => {
  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const headingColor = useColorModeValue('gray.800', 'white');

  const baseUrl = "https://abhishekbhalerao.com"; 

  return (

    <>
      <SEO 
        title="Abhishek Bhalerao | Full Stack Developer & Robotics Engineer"
        description="Full Stack Developer specializing in Industrial Automation and Robotics Engineering, creating innovative solutions that connect digital systems with physical hardware."
        keywords="Full Stack Developer, Industrial Automation, Robotics Engineering, Software Development, Hardware Integration, React, Node.js, Python, ESP32, Arduino, Web Development"
        ogImage={`${baseUrl}/images/abhishek_bhalerao_og.jpg`}
        twitterImage={`${baseUrl}/images/abhishek_bhalerao_og.jpg`}
        canonicalUrl={baseUrl}
      />

    <Container maxW="container.xl" py={8} mt="120px">

      {/* Hero Section */}
      <Box
        p={{ base: 8, md: 12 }}
        borderRadius="xl"
        color="white"
        mb={12}
        position="relative"
        overflow="hidden"
        boxShadow="xl"
        sx={{
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(135deg, #3182CE 0%, #2C5282 100%)",
            zIndex: -2,
          },
          "@keyframes float": {
            "0%": { transform: "translateY(0px)" },
            "50%": { transform: "translateY(-20px)" },
            "100%": { transform: "translateY(0px)" }
          },
          "@keyframes pulse": {
            "0%": { opacity: 0.5, transform: "scale(1)" },
            "50%": { opacity: 0.8, transform: "scale(1.05)" },
            "100%": { opacity: 0.5, transform: "scale(1)" }
          },
          "@keyframes gradientAnimation": {
            "0%": { backgroundPosition: "0% 50%" },
            "50%": { backgroundPosition: "100% 50%" },
            "100%": { backgroundPosition: "0% 50%" }
          }
        }}
      >
        {/* Animated gradient background */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          background="linear-gradient(-45deg, #3182CE, #4299E1, #2B6CB0, #2C5282)"
          backgroundSize="400% 400%"
          zIndex="-1"
          sx={{
            animation: "gradientAnimation 15s ease infinite",
          }}
        />
        
        {/* Animated dots pattern */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          opacity="0.1"
          backgroundImage={`url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`}
          zIndex="-1"
        />
        
        {/* Floating decorative elements */}
        <Box
          position="absolute"
          top="-20px"
          right="-20px"
          width="200px"
          height="200px"
          borderRadius="full"
          bg="whiteAlpha.100"
          zIndex="0"
          sx={{ animation: "pulse 8s infinite ease-in-out" }}
        />
        <Box
          position="absolute"
          bottom="-30px"
          left="-30px"
          width="150px"
          height="150px"
          borderRadius="full"
          bg="whiteAlpha.100"
          zIndex="0"
          sx={{ animation: "pulse 6s infinite ease-in-out" }}
        />
        
        {/* Additional floating elements */}
        <Box
          position="absolute"
          top="20%"
          right="15%"
          width="80px"
          height="80px"
          borderRadius="full"
          bg="whiteAlpha.100"
          zIndex="0"
          sx={{ animation: "float 7s infinite ease-in-out" }}
        />
        <Box
          position="absolute"
          bottom="25%"
          left="10%"
          width="120px"
          height="120px"
          borderRadius="full"
          bg="whiteAlpha.100"
          zIndex="0"
          sx={{ animation: "float 9s infinite ease-in-out" }}
        />
        
        {/* Subtle geometric shapes */}
        <Box
          position="absolute"
          top="15%"
          left="20%"
          width="100px"
          height="100px"
          transform="rotate(45deg)"
          bg="whiteAlpha.50"
          zIndex="0"
          sx={{ animation: "pulse 10s infinite ease-in-out" }}
        />
        <Box
          position="absolute"
          bottom="20%"
          right="25%"
          width="70px"
          height="70px"
          borderRadius="lg"
          bg="whiteAlpha.50"
          zIndex="0"
          sx={{ animation: "float 8s infinite ease-in-out" }}
        />
        
        {/* Content */}
        <Box maxW="800px" mx="auto" position="relative" zIndex="1">
          <VStack align="center" spacing={8} textAlign="center">
            <Heading 
              as="h1" 
              size="2xl" 
              lineHeight="1.2"
              fontWeight="bold"
              letterSpacing="tight"
            >
              <Box as="span" display="block" mb={2}>
                Bridging the Gap Between
              </Box>
              <Box as="span" fontSize={{ base: "4xl", md: "6xl" }} fontWeight="extrabold">
                <Box as="span" color="white">Software</Box>{" "}
                <Box as="span" color="blue.200">🤝</Box>{" "}
                <Box as="span" color="white">Hardware</Box>
              </Box>
            </Heading>
            
            <Text 
              fontSize={{ base: "lg", md: "xl" }} 
              maxW="650px" 
              opacity="0.9"
              lineHeight="tall"
            >
              Full Stack Developer specializing in Industrial Automation and Robotics Engineering, 
              creating innovative solutions that connect digital systems with physical hardware.
            </Text>
            
            <HStack spacing={5} pt={4}>
              <Button
                as={RouterLink}
                to="/about"
                size="lg"
                colorScheme="whiteAlpha"
                variant="outline"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: 'whiteAlpha.200' }}
                fontWeight="medium"
                px={8}
                borderWidth="2px"
              >
                About Me
              </Button>
              <Button
                as={RouterLink}
                to="/projects"
                size="lg"
                colorScheme="blue"
                bg="white"
                color="blue.600"
                _hover={{ bg: 'gray.100' }}
                fontWeight="medium"
                px={8}
                boxShadow="md"
              >
                View Projects
              </Button>
            </HStack>
            
            {/* Tech icons */}
            <HStack 
              spacing={{ base: 4, md: 8 }} 
              pt={6} 
              opacity="0.8" 
              wrap="wrap" 
              justify="center"
            >
              <Icon as={FaCode} w={6} h={6} />
              <Icon as={FaMicrochip} w={6} h={6} />
              <Icon as={FaWrench} w={6} h={6} />
            </HStack>
          </VStack>
        </Box>
      </Box>

      {/* Expertise Section */}
      <Heading as="h2" size="xl" mb={6} textAlign="center">
        Areas of Expertise
      </Heading>
      
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} mb={12}>
        <Box
          p={6}
          borderRadius="lg"
          bg={bgColor}
          boxShadow="md"
          borderWidth="1px"
          borderColor={borderColor}
          transition="transform 0.2s"
          _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
        >
          <Icon as={FaCode} w={10} h={10} color="blue.500" mb={4} />
          <Heading as="h3" size="md" mb={2}>Software Development</Heading>
          <Text>Full stack development with modern technologies including React, Node.js, Python and REST APIs.</Text>
        </Box>

        <Box
          p={6}
          borderRadius="lg"
          bg={bgColor}
          boxShadow="md"
          borderWidth="1px"
          borderColor={borderColor}
          transition="transform 0.2s"
          _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
        >
          <Icon as={FaWrench} w={10} h={10} color="blue.500" mb={4} />
          <Heading as="h3" size="md" mb={2}>Industrial Automation</Heading>
          <Text>Designing and implementing automation solutions for industrial applications and processes.</Text>
        </Box>

        <Box
          p={6}
          borderRadius="lg"
          bg={bgColor}
          boxShadow="md"
          borderWidth="1px"
          borderColor={borderColor}
          transition="transform 0.2s"
          _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
        >
          <Icon as={FaMicrochip} w={10} h={10} color="blue.500" mb={4} />
          <Heading as="h3" size="md" mb={2}>Robotics Engineering</Heading>
          <Text>Engineering robotic solutions and integrating hardware with software systems.</Text>
        </Box>
      </SimpleGrid>

      {/* Call to Action */}
      <Box
        p={10}
        borderRadius="xl"
        bg={useColorModeValue('blue.50', 'blue.900')}
        borderWidth="1px"
        borderColor={borderColor}
        textAlign="center"
        boxShadow="md"
      >
        <Heading as="h2" size="xl" mb={4} color={headingColor}>Let's build something amazing together</Heading>
        <Text fontSize="lg" mb={6} maxW="800px" mx="auto" color={textColor}>
          Whether you need a full-stack application, industrial automation solution or robotics integration.
          I bring a unique blend of software and hardware expertise to every project.
        </Text>
        <HStack spacing={4} justify="center">
          <Button
            as={RouterLink}
            to="/contact"
            size="lg"
            colorScheme="blue"
            rightIcon={<FaArrowRight />}
          >
            Get in Touch
          </Button>
          <Button
            as={RouterLink}
            to="/projects"
            size="lg"
            variant="outline"
            colorScheme="blue"
          >
            View My Work
          </Button>
        </HStack>
      </Box>
    </Container>
    </>
  );
};

export default Home;






